<template>
  <div class="cache-policies h-100">
    <div class="table-wrapper">
      <div class="table-header">
        <div class="table-header__title">
          <p class="text">
            Cache Policies
          </p>
          <p class="text">
            You can use cache policies to improve your cache hit ratio by
            controlling the values (URL, query strings, HTTP headers, and
            cookies) that are included in the cache key. Captain CDN provides
            some system cache policies for most common use cases. You can use
            these templates when you create a distribution at the cache behavior
            settings or you can create your own cache policies specific to your
            needs.
          </p>
        </div>
        <b-button
          v-if="$can('create', 'cachepolicies', 'cdn')"
          variant="bg-primary"
          @click="$router.push({ name: 'cache-policy-create' })"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create cache policy
        </b-button>
        <b-button
          v-if="$can('update', 'cachepolicies', 'cdn')"
          variant="bg-white"
          :disabled="isEditable"
          class="d-none d-lg-inline"
          @click="onEditClick"
        >
          Edit
        </b-button>
        <button-loading
          v-if="$can('delete', 'cachepolicies', 'cdn')"
          :spinning="removingPolicy"
          variant="bg-white"
          :disabled="isEditable"
          class="d-none d-lg-inline"
          @click="onDeleteClick"
        >
          Delete
        </button-loading>
      </div>
      <div class="default-table d-none d-lg-block">
        <div class="position-relative">
          <b-table
            ref="table"
            :items="cachePolicies"
            :fields="fields"
            :class="{ loaded: !loading }"
            select-mode="single"
            show-empty
            selectable
            responsive
            @row-selected="onRowSelected"
          >
            <template #cell(selected)="{ rowSelected }">
              <select-button :selected="rowSelected" />
            </template>

            <template #cell(name)="data">
              <a
                class="clickable"
                @click="onNameClick(data.item.id)"
              >{{
                data.value
              }}</a>
            </template>

            <template #cell(lastModified)="data">
              {{ formatOffsetDateTime(data.value) }}
            </template>

            <template #cell(type)="data">
              <template v-if="data.item.isGjirafaPolicy">
                SYSTEM
              </template>
              <template v-else>
                CUSTOM
              </template>
            </template>

            <template #empty>
              <div
                v-if="loading"
                class="loading-table text-center py-3"
              >
                <b-spinner variant="primary" />
              </div>
              <template v-else>
                <no-data
                  no-data-text="No cache policy have been added yet..."
                  create-text="+ Create Cache Policy"
                  access-page="Cache Policies"
                  :has-permissions="hasPermissions"
                  @action="$router.push({ name: 'cache-policy-create' })"
                />
              </template>
            </template>
          </b-table>
        </div>
        <div
          v-if="!loading && totalCount > 0"
          class="default-pagination"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="pageSize"
            first-number
            last-number
          />
          <div
            v-if="first !== undefined && last !== undefined && totalCount !== undefined"
            class="results"
          >
            Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
          </div>
        </div>
      </div>
    </div>

    <!-- MOBILE -->
    <div class="position-relative d-lg-none mt-2">
      <div :class="{ 'opacity-7': loading }">
        <template v-for="policy in cachePolicies">
          <card
            :key="policy.id"
            :fields="fields"
            :item="policy"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                v-if="!policy.isGjirafaPolicy"
                id="dropdown-1"
                right
                variant="white"
                @show="select(policy)"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'cachepolicies', 'cdn')"
                  @click="onEditClick"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'cachepolicies', 'cdn')"
                  @click="onDeleteClick"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
            <template #name>
              <a
                class="link"
                @click="onNameClick(policy.id)"
              >{{
                policy.name
              }}</a>
            </template>
            <template #lastModified>
              {{ formatOffsetDateTime(policy.lastModified) }}
            </template>
            <template #type>
              <template v-if="policy.isGjirafaPolicy">
                SYSTEM
              </template>
              <template v-else>
                CUSTOM
              </template>
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="!loading && cachePolicies.length < 1">
        <paper-simple class="top-fade-in">
          <no-data
            class="text-center"
            no-data-text="No cache policy have been added yet..."
            create-text="+ Create Cache Policy"
            access-page="Cache Policies"
            :has-permissions="hasPermissions"
            @action="$router.push({ name: 'cache-policy-create' })"
          />
        </paper-simple>
      </template>
      <div
        v-if="!loading && totalCount > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div
          v-if="first !== undefined && last !== undefined && totalCount !== undefined"
          class="results"
        >
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>

    <info
      v-if="openInfo"
      :title="infoTitle"
      :message="infoMessage"
      @close="openInfo = false"
    />
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import messages from '../../messages'

export default {
  name: 'CachePolicy',
  components: {
    Card,
  },
  data() {
    return {
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      selected: {},
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'name',
          label: 'name',
          variant: 'fill',
          isPrimary: true,
        },
        { key: 'comment', isPrimary: true },
        'lastModified',
        'distributions',
        'id',
        'type',
      ],
      policy: null,
      hasPermissions: true,
      currentPage: 1,
    }
  },
  computed: {
    cachePolicies() {
      return this.$store.state.cachePolicies.cachePolicies
    },
    cachePolicyPagination() {
      return this.$store.state.cachePolicies.cachePolicyPagination
    },
    first() {
      return this.cachePolicyPagination.pageIndex * this.cachePolicyPagination.pageSize + 1
    },
    last() {
      return this.currentPage * this.cachePolicyPagination.pageSize < this.cachePolicyPagination.totalCount
        ? this.currentPage * this.cachePolicyPagination.pageSize
        : this.cachePolicyPagination.totalCount
    },
    totalCount() {
      return this.cachePolicyPagination.totalCount
    },
    pageSize() {
      return this.cachePolicyPagination.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    loading() {
      return this.$store.state.cachePolicies.fetchingPolicies
    },
    isSelected() {
      return this.selected[0]
    },
    isEditable() {
      if (this.selected[0]) {
        return this.selected[0].isGjirafaPolicy
      }
      return true
    },
    removingPolicy() {
      return this.$store.state.cachePolicies.removingPolicy
    },
  },
  watch: {
    currentPage() {
      this.fetchCachePolicies()
    },
  },
  created() {
    this.id = this.$route.params.id
    this.fetchCachePolicies()
  },
  methods: {
    select(policy) {
      this.selected = policy !== null ? [policy] : []
    },
    fetchCachePolicies() {
      const pagination = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      }
      this.$store.dispatch('cachePolicies/fetchCachePolicies', pagination).catch(error => {
        if (error.response?.status === 403) {
          this.hasPermissions = false
          return
        }
        this.errorToast(
        error.response?.data?.errors[0]
          || 'Something went wrong while fetching cache policies.',
        )
      })
    },
    onRowSelected(item) {
      this.selected = item
    },
    onEditClick() {
      if (this.isSelected) {
        this.$router.push({
          name: 'cache-policy-edit',
          params: { cacheId: this.selected[0].id },
        })
      }
    },
    onDeleteClick() {
      this.confirmDelete(
        'Delete Cache Policy',
        'Are you sure you want to delete this cache policy?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('cachePolicies/removeCachePolicy', this.selected[0].id)
            .then(() => {
              this.fetchCachePolicies()
              this.successToast('Cache policy was removed')
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
    onNameClick(cacheId) {
      this.policy = cacheId
      this.$router.push({
        name: 'cache-policy-info',
        params: { cacheId },
        query: { v: 'info' },
      })
    },
    showInformation(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
  },
}
</script>
